import { useState } from "react";

/* ── Exercise metadata ── */
const exMeta = {
  "Bodyweight Squats":          { icon: "🏋️", muscles: ["Quads","Glutes"] },
  "Leg Swings (front-to-back)": { icon: "🦵", muscles: ["Hip Flexors"] },
  "Ankle Circles":              { icon: "🦶", muscles: ["Calves"] },
  "Barbell Back Squat":         { icon: "🏋️", muscles: ["Quads","Glutes","Core"] },
  "Bulgarian Split Squat":      { icon: "🏋️", muscles: ["Quads","Glutes"] },
  "Leg Press (feet low & narrow)": { icon: "⚙️", muscles: ["Quads","Glutes"] },
  "Walking Lunges":             { icon: "🚶", muscles: ["Quads","Glutes"] },
  "Dead Bug":                   { icon: "🎯", muscles: ["Abs","TVA"] },
  "Plank (front)":              { icon: "🎯", muscles: ["Abs","Obliques"] },
  "Incline Treadmill Walk":     { icon: "🏃", muscles: ["Glutes","Calves"] },
  "Glute Bridge (bodyweight)":  { icon: "🍑", muscles: ["Glutes","Hams"] },
  "Lateral Band Walks":         { icon: "🔴", muscles: ["Glute Med"] },
  "Cat-Cow Stretch":            { icon: "🧘", muscles: ["Spine","Core"] },
  "Romanian Deadlift (RDL)":    { icon: "🏋️", muscles: ["Hamstrings","Glutes"] },
  "Hip Thrust (barbell)":       { icon: "🏋️", muscles: ["Glutes","Hams"] },
  "Seated Leg Curl":            { icon: "⚙️", muscles: ["Hamstrings"] },
  "Cable Pull-Through":         { icon: "🔗", muscles: ["Glutes","Hams"] },
  "Pallof Press":               { icon: "🔗", muscles: ["Obliques","Abs"] },
  "Side Plank":                 { icon: "🎯", muscles: ["Obliques","QL"] },
  "Rowing Machine Intervals":   { icon: "🚣", muscles: ["Hams","Glutes","Cardio"] },
  "Jump Rope (or Jumping Jacks)": { icon: "⚡", muscles: ["Calves","Cardio"] },
  "Bodyweight Reverse Lunges":  { icon: "🦵", muscles: ["Quads","Glutes"] },
  "Hip 90/90 Stretch":          { icon: "🧘", muscles: ["Hips","Glutes"] },
  "Trap Bar Deadlift":          { icon: "🏋️", muscles: ["Quads","Glutes","Hams"] },
  "Box Jumps":                  { icon: "📦", muscles: ["Quads","Glutes","Calves"] },
  "Goblet Squat (tempo 3-1-1)": { icon: "🏋️", muscles: ["Quads","Core"] },
  "Single-Leg Calf Raise":     { icon: "🦶", muscles: ["Calves"] },
  "Ab Wheel Rollout (or Barbell Rollout)": { icon: "🎯", muscles: ["Abs"] },
  "Hollow Body Hold":           { icon: "🎯", muscles: ["Abs","Hip Flexors"] },
  "Assault/Air Bike Intervals": { icon: "🚴", muscles: ["Quads","Hams","Cardio"] },
  "Single-Leg Balance":         { icon: "🧘", muscles: ["Glutes","Calves"] },
  "Inchworms":                  { icon: "🐛", muscles: ["Hams","Core"] },
  "Banded Clamshells":          { icon: "🔴", muscles: ["Glute Med"] },
  "Single-Leg Romanian Deadlift (DB)": { icon: "🏋️", muscles: ["Hamstrings","Glutes"] },
  "Step-Ups (weighted, knee-height box)": { icon: "📦", muscles: ["Glutes","Quads"] },
  "Lateral Lunge":              { icon: "🦵", muscles: ["Adductors","Quads"] },
  "Stability Ball Hamstring Curl": { icon: "🟡", muscles: ["Hamstrings","Glutes"] },
  "Bird Dog":                   { icon: "🎯", muscles: ["Core","Glutes"] },
  "Reverse Crunch":             { icon: "🎯", muscles: ["Lower Abs"] },
  "Stairclimber (steady pace)": { icon: "🪜", muscles: ["Glutes","Calves","Cardio"] },
};
const getMeta = (name) => exMeta[name] || { icon: "💪", muscles: [] };

const tagStyle = (m) => {
  const map = {
    Quads: "#3B82F6", Glutes: "#EC4899", Hamstrings: "#F59E0B", Hams: "#F59E0B",
    Calves: "#10B981", Core: "#8B5CF6", Abs: "#8B5CF6", TVA: "#8B5CF6",
    Obliques: "#A855F7", QL: "#A855F7", "Lower Abs": "#8B5CF6",
    "Hip Flexors": "#F97316", Hips: "#F97316", Adductors: "#06B6D4",
    "Glute Med": "#EC4899", Spine: "#6366F1", Cardio: "#EF4444",
  };
  const c = map[m] || "#6366F1";
  return { background: c + "18", color: c, borderColor: c + "30" };
};

/* ── Session templates ── */
const templates = [
  { type: "A", name: "Quad & Core Power", focus: "Quadriceps-dominant + anterior core", emoji: "🦵", color: "#E8553A",
    blocks: [
      { label: "WARM-UP", time: "3 min", exercises: [
        { name: "Bodyweight Squats", sets: "1", reps: "15", note: "Slow & controlled, full ROM (Range of Motion)" },
        { name: "Leg Swings (front-to-back)", sets: "1", reps: "10/leg", note: "Dynamic hip flexor activation" },
        { name: "Ankle Circles", sets: "1", reps: "10/side", note: "Prep ankle dorsiflexion" },
      ]},
      { label: "MAIN WORK", time: "22 min", exercises: [
        { name: "Barbell Back Squat", sets: "4", reps: "8–10", rest: "90s", note: "EMG (Electromyography) studies show ~80% quad activation at parallel depth. Heels planted, chest up." },
        { name: "Bulgarian Split Squat", sets: "3", reps: "10/leg", rest: "60s", note: "18% higher quad activation vs. standard lunges (Mackey & Riemann, 2021). Rear foot elevated on bench." },
        { name: "Leg Press (feet low & narrow)", sets: "3", reps: "12", rest: "60s", note: "Low foot placement shifts ~15% more load to quads vs. high placement." },
        { name: "Walking Lunges", sets: "2", reps: "12/leg", rest: "60s", note: "Step short for quads, long for glutes — go short today." },
      ]},
      { label: "CORE", time: "5 min", exercises: [
        { name: "Dead Bug", sets: "3", reps: "10/side", rest: "30s", note: "Activates Transverse Abdominis (TVA) 40% more than crunches. Press lower back into floor." },
        { name: "Plank (front)", sets: "2", reps: "30–40s", rest: "20s", note: "McGill study: max core activation with minimal spinal load — safer than sit-ups." },
      ]},
      { label: "CARDIO FINISHER", time: "10 min", exercises: [
        { name: "Incline Treadmill Walk", sets: "1", reps: "10 min", note: "12–15% incline, 5.5–6.5 km/h. Burns ~30–40% more calories than flat walking (ACSM data)." },
      ]},
    ],
  },
  { type: "B", name: "Glute & Hamstring Focus", focus: "Posterior chain + obliques", emoji: "🍑", color: "#D97706",
    blocks: [
      { label: "WARM-UP", time: "3 min", exercises: [
        { name: "Glute Bridge (bodyweight)", sets: "1", reps: "15", note: "Squeeze glutes 2s at top" },
        { name: "Lateral Band Walks", sets: "1", reps: "10/side", note: "Activate Gluteus Medius for hip stability" },
        { name: "Cat-Cow Stretch", sets: "1", reps: "8", note: "Spinal mobility prep" },
      ]},
      { label: "MAIN WORK", time: "22 min", exercises: [
        { name: "Romanian Deadlift (RDL)", sets: "4", reps: "8–10", rest: "90s", note: "~85% MVIC (Maximum Voluntary Isometric Contraction) in hamstrings. Hinge at hips, soft knees." },
        { name: "Hip Thrust (barbell)", sets: "3", reps: "10–12", rest: "60s", note: "Contreras et al. (2015): higher Gluteus Maximus EMG than squats. Pause 1s at top." },
        { name: "Seated Leg Curl", sets: "3", reps: "12", rest: "60s", note: "Seated stretches hamstrings more, increasing activation by ~11%." },
        { name: "Cable Pull-Through", sets: "3", reps: "12", rest: "45s", note: "Low-impact glute/hamstring drill. Powerful hip extension." },
      ]},
      { label: "CORE", time: "5 min", exercises: [
        { name: "Pallof Press", sets: "3", reps: "10/side", rest: "30s", note: "Obliques 35% more activated than Russian Twists, with lower spinal shear." },
        { name: "Side Plank", sets: "2", reps: "25–30s/side", rest: "20s", note: "Targets Quadratus Lumborum (QL) + obliques — key for lower back health." },
      ]},
      { label: "CARDIO FINISHER", time: "10 min", exercises: [
        { name: "Rowing Machine Intervals", sets: "1", reps: "10 min", note: "30s hard / 30s easy. Hamstring + glute 60–70% MVIC. Heart rate Zone 3–4." },
      ]},
    ],
  },
  { type: "C", name: "Lower Body Power", focus: "Explosive strength + deep core", emoji: "⚡", color: "#7C3AED",
    blocks: [
      { label: "WARM-UP", time: "3 min", exercises: [
        { name: "Jump Rope (or Jumping Jacks)", sets: "1", reps: "60s", note: "Elevate HR, prep CNS (Central Nervous System)" },
        { name: "Bodyweight Reverse Lunges", sets: "1", reps: "8/leg", note: "Dynamic prep for explosive movements" },
        { name: "Hip 90/90 Stretch", sets: "1", reps: "30s/side", note: "Hip internal/external rotation" },
      ]},
      { label: "MAIN WORK", time: "22 min", exercises: [
        { name: "Trap Bar Deadlift", sets: "4", reps: "6–8", rest: "90s", note: "JSCR (Journal of Strength & Conditioning Research): ~10% higher peak power than conventional deadlifts." },
        { name: "Box Jumps", sets: "3", reps: "8", rest: "60s", note: "Improves vertical jump 7–12% in 6 weeks. Step down to reduce impact." },
        { name: "Goblet Squat (tempo 3-1-1)", sets: "3", reps: "10", rest: "60s", note: "3s down / 1s pause / 1s up. TUT (Time Under Tension) boosts protein synthesis ~30%." },
        { name: "Single-Leg Calf Raise", sets: "3", reps: "15/leg", rest: "30s", note: "Ankle stability + power transfer. Full stretch at bottom." },
      ]},
      { label: "CORE", time: "5 min", exercises: [
        { name: "Ab Wheel Rollout (or Barbell Rollout)", sets: "3", reps: "8–10", rest: "30s", note: "Rectus Abdominis ~90% MVIC — among the highest of any core exercise." },
        { name: "Hollow Body Hold", sets: "2", reps: "25–30s", rest: "20s", note: "Gymnastic staple. Anti-extension = core stability foundation." },
      ]},
      { label: "CARDIO FINISHER", time: "10 min", exercises: [
        { name: "Assault/Air Bike Intervals", sets: "1", reps: "10 min", note: "20s all-out / 40s recovery. HIIT (High-Intensity Interval Training) burns 25–30% more calories (Boutcher, 2011)." },
      ]},
    ],
  },
  { type: "D", name: "Stability & Unilateral", focus: "Balance + imbalance correction + core", emoji: "🎯", color: "#0891B2",
    blocks: [
      { label: "WARM-UP", time: "3 min", exercises: [
        { name: "Single-Leg Balance", sets: "1", reps: "20s/leg", note: "Eyes closed for advanced — activates proprioceptors" },
        { name: "Inchworms", sets: "1", reps: "5", note: "Full-body dynamic stretch" },
        { name: "Banded Clamshells", sets: "1", reps: "12/side", note: "Isolate Gluteus Medius — critical for knee tracking" },
      ]},
      { label: "MAIN WORK", time: "22 min", exercises: [
        { name: "Single-Leg Romanian Deadlift (DB)", sets: "4", reps: "8/leg", rest: "60s", note: "Corrects 5–15% strength asymmetry most people have between legs." },
        { name: "Step-Ups (weighted, knee-height box)", sets: "3", reps: "10/leg", rest: "60s", note: "JSCR 2020: top 3 for Gluteus Maximus activation at 88% MVIC." },
        { name: "Lateral Lunge", sets: "3", reps: "10/side", rest: "60s", note: "Strengthens adductors, reducing groin injury risk by 41% (Harøy et al., 2019)." },
        { name: "Stability Ball Hamstring Curl", sets: "3", reps: "12", rest: "45s", note: "Knee flexion + hip extension on unstable surface = deep stabilizer work." },
      ]},
      { label: "CORE", time: "5 min", exercises: [
        { name: "Bird Dog", sets: "3", reps: "8/side", rest: "30s", note: "McGill's 'Big 3' for spinal health. Anti-rotation + anti-extension." },
        { name: "Reverse Crunch", sets: "2", reps: "15", rest: "20s", note: "Lower Rectus Abdominis. Lift hips, don't just swing legs." },
      ]},
      { label: "CARDIO FINISHER", time: "10 min", exercises: [
        { name: "Stairclimber (steady pace)", sets: "1", reps: "10 min", note: "~0.17 kcal/step (Harvard Health). ~85 kcal in 10 min. Zero eccentric quad stress." },
      ]},
    ],
  },
];

/* ── Build 10 dated sessions: Apr 14–30 ── */
const schedule = [
  { date: "Tue, Apr 14", week: 1, templateIdx: 0, id: 1 },
  { date: "Thu, Apr 16", week: 1, templateIdx: 1, id: 2 },
  { date: "Sat, Apr 18", week: 1, templateIdx: 2, id: 3 },
  { date: "Mon, Apr 20", week: 1, templateIdx: 3, id: 4 },
  { date: "Tue, Apr 21", week: 2, templateIdx: 0, id: 5 },
  { date: "Thu, Apr 23", week: 2, templateIdx: 1, id: 6 },
  { date: "Sat, Apr 25", week: 2, templateIdx: 2, id: 7 },
  { date: "Mon, Apr 27", week: 2, templateIdx: 3, id: 8 },
  { date: "Tue, Apr 29", week: 3, templateIdx: 0, id: 9 },
  { date: "Thu, Apr 30", week: 3, templateIdx: 1, id: 10 },
];

const sessions = schedule.map(s => {
  const t = templates[s.templateIdx];
  return { ...s, ...t };
});

/* ── Calendar data for April 2026 ── */
const aprilDays = Array.from({ length: 30 }, (_, i) => i + 1);
const firstDayOffset = 2; // Apr 1, 2026 = Wednesday → offset 2 (Mon-based: W=2)
const workoutDays = { 14: 0, 16: 1, 18: 2, 20: 3, 21: 0, 23: 1, 25: 2, 27: 3, 29: 0, 30: 1 };
const dayLabels = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const tagColors = {
  "WARM-UP": { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
  "MAIN WORK": { bg: "#DCFCE7", text: "#166534", border: "#BBF7D0" },
  "CORE": { bg: "#E0E7FF", text: "#3730A3", border: "#C7D2FE" },
  "CARDIO FINISHER": { bg: "#FFE4E6", text: "#9F1239", border: "#FECDD3" },
};

function ExerciseCard({ ex, idx }) {
  const [open, setOpen] = useState(false);
  const meta = getMeta(ex.name);
  return (
    <div onClick={() => setOpen(!open)} style={{ padding: "11px 14px", background: open ? "#F8FAFC" : "#fff", borderBottom: "1px solid #F1F5F9", cursor: "pointer", transition: "background 0.15s" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 10, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 20 }}>{meta.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 4 }}>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13.5, color: "#1E293B", lineHeight: 1.3 }}>
              <span style={{ color: "#94A3B8", fontWeight: 500, marginRight: 5, fontSize: 11 }}>{String(idx + 1).padStart(2, "0")}</span>
              {ex.name}
            </div>
            <span style={{ fontSize: 14, color: "#CBD5E1", transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s", flexShrink: 0 }}>▾</span>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
            {ex.sets && <span style={{ fontSize: 11.5, color: "#64748B", fontFamily: "'JetBrains Mono', monospace" }}>{ex.sets} × {ex.reps}</span>}
            {ex.rest && <span style={{ fontSize: 10.5, color: "#94A3B8", fontFamily: "'JetBrains Mono', monospace" }}>rest {ex.rest}</span>}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 5, flexWrap: "wrap" }}>
            {meta.muscles.map(m => {
              const ts = tagStyle(m);
              return <span key={m} style={{ fontSize: 9.5, fontWeight: 600, padding: "2px 7px", borderRadius: 5, background: ts.background, color: ts.color, border: `1px solid ${ts.borderColor}` }}>{m}</span>;
            })}
          </div>
        </div>
      </div>
      {open && ex.note && (
        <div style={{ marginTop: 10, marginLeft: 54, padding: "10px 12px", background: "#F1F5F9", borderRadius: 8, fontSize: 12.5, lineHeight: 1.55, color: "#475569", fontFamily: "'DM Sans', sans-serif", borderLeft: "3px solid #6366F1" }}>
          💡 {ex.note}
        </div>
      )}
    </div>
  );
}

function Calendar({ activeDay, onSelectDay }) {
  const cells = [];
  for (let i = 0; i < firstDayOffset; i++) cells.push(<div key={`e${i}`} />);
  aprilDays.forEach(d => {
    const hasWorkout = workoutDays[d] !== undefined;
    const tIdx = workoutDays[d];
    const isActive = activeDay === d;
    const color = hasWorkout ? templates[tIdx].color : "transparent";
    const today = d === 14;
    cells.push(
      <div
        key={d}
        onClick={() => hasWorkout && onSelectDay(d)}
        style={{
          width: "100%", aspectRatio: "1", borderRadius: 8, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", cursor: hasWorkout ? "pointer" : "default",
          background: isActive ? color : hasWorkout ? color + "20" : "transparent",
          color: isActive ? "#fff" : hasWorkout ? color : "#94A3B8",
          fontWeight: hasWorkout ? 700 : 400, fontSize: 12, fontFamily: "'DM Sans', sans-serif",
          border: today && !isActive ? `2px solid ${color || "#6366F1"}` : isActive ? "none" : "1px solid transparent",
          transition: "all 0.15s",
          position: "relative",
        }}
      >
        {d}
        {hasWorkout && !isActive && <div style={{ width: 4, height: 4, borderRadius: 2, background: color, marginTop: 2 }} />}
        {hasWorkout && isActive && <div style={{ fontSize: 8, marginTop: 1 }}>{templates[tIdx].emoji}</div>}
      </div>
    );
  });
  return (
    <div style={{ padding: "0 20px 8px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, textAlign: "center" }}>
        {dayLabels.map(d => <div key={d} style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600, padding: "4px 0", fontFamily: "'DM Sans', sans-serif" }}>{d}</div>)}
        {cells}
      </div>
    </div>
  );
}

function SessionPill({ s, isActive, onClick }) {
  return (
    <div onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, cursor: "pointer",
      background: isActive ? s.color : "#fff", color: isActive ? "#fff" : "#475569",
      border: isActive ? "none" : "1px solid #E2E8F0", transition: "all 0.15s", flexShrink: 0,
      minWidth: 0,
    }}>
      <span style={{ fontSize: 16 }}>{s.emoji}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap" }}>{s.date}</div>
        <div style={{ fontSize: 9.5, opacity: isActive ? 0.85 : 0.6, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap" }}>{s.name}</div>
      </div>
    </div>
  );
}

export default function WorkoutPlan() {
  const [activeIdx, setActiveIdx] = useState(0);
  const s = sessions[activeIdx];

  const dayToIdx = {};
  sessions.forEach((sess, i) => {
    const dayNum = parseInt(sess.date.split(" ")[1]);
    dayToIdx[dayNum] = i;
  });

  const activeDay = parseInt(s.date.split(" ")[1]);

  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC", fontFamily: "'DM Sans', sans-serif", margin: "-16px -16px 0", borderRadius: 0 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet" />

      <div style={{ background: "#0F172A", padding: "24px 20px 18px", color: "#fff" }}>
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: "#94A3B8", fontWeight: 600, marginBottom: 5 }}>April 2026 Program</div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>Lower Body & Core</h1>
        <div style={{ display: "flex", gap: 14, marginTop: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "#94A3B8" }}>🔥 10 sessions</span>
          <span style={{ fontSize: 11, color: "#94A3B8" }}>⏱ 40 min each</span>
          <span style={{ fontSize: 11, color: "#94A3B8" }}>🌅 Morning</span>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          {templates.map(t => (
            <div key={t.type} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: 3, background: t.color }} />
              <span style={{ fontSize: 9.5, color: "#94A3B8" }}>{t.emoji} {t.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: "16px 0 4px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1E293B", padding: "0 20px 8px" }}>April 2026</div>
        <Calendar activeDay={activeDay} onSelectDay={(d) => { if (dayToIdx[d] !== undefined) setActiveIdx(dayToIdx[d]); }} />
      </div>

      <div style={{ display: "flex", gap: 6, padding: "8px 16px", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        {sessions.map((sess, i) => <SessionPill key={sess.id} s={sess} isActive={i === activeIdx} onClick={() => setActiveIdx(i)} />)}
      </div>

      <div style={{ padding: "8px 20px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: s.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{s.emoji}</div>
          <div>
            <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>{s.date} · Session {s.id}/10</div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1E293B" }}>{s.name}</h2>
            <p style={{ margin: "1px 0 0", fontSize: 12, color: "#64748B" }}>{s.focus}</p>
          </div>
        </div>
        {s.week === 2 && s.templateIdx === 0 && (
          <div style={{ marginTop: 8, padding: "6px 10px", background: "#F0FDF4", borderRadius: 8, border: "1px solid #BBF7D0", fontSize: 11, color: "#166534" }}>
            📈 Week 2 — try increasing weights by 2–5% from last week's lifts
          </div>
        )}
        {s.week === 3 && s.templateIdx === 0 && (
          <div style={{ marginTop: 8, padding: "6px 10px", background: "#FFF7ED", borderRadius: 8, border: "1px solid #FED7AA", fontSize: 11, color: "#9A3412" }}>
            🏁 Final week — push hard, you've built a solid base over 2+ weeks!
          </div>
        )}
      </div>

      <div style={{ padding: "0 16px 30px" }}>
        {s.blocks.map((block, bi) => {
          const tc = tagColors[block.label] || tagColors["MAIN WORK"];
          return (
            <div key={bi} style={{ marginBottom: 16, borderRadius: 14, overflow: "hidden", border: "1px solid #E2E8F0", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #F1F5F9" }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, padding: "3px 10px", borderRadius: 6, background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}>{block.label}</span>
                <span style={{ fontSize: 12, color: "#94A3B8", fontFamily: "'JetBrains Mono', monospace" }}>{block.time}</span>
              </div>
              {block.exercises.map((ex, ei) => <ExerciseCard key={ei} ex={ex} idx={ei} />)}
            </div>
          );
        })}
      </div>

      <div style={{ padding: "0 20px 16px" }}>
        <div style={{ padding: "14px", background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#1E293B", marginBottom: 8 }}>📊 April Progress</div>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {sessions.map((_, i) => (
              <div key={i} style={{
                flex: 1, height: 6, borderRadius: 3,
                background: i <= activeIdx ? sessions[i].color : "#E2E8F0",
                transition: "background 0.2s",
              }} />
            ))}
          </div>
          <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 6, textAlign: "center" }}>
            {activeIdx + 1} of 10 sessions · {((activeIdx + 1) / 10 * 100).toFixed(0)}% complete
          </div>
        </div>
      </div>

      <div style={{ padding: "0 20px 40px", textAlign: "center" }}>
        <div style={{ padding: "16px", background: "#FFFBEB", borderRadius: 12, border: "1px solid #FDE68A" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#92400E", marginBottom: 4 }}>💪 Progressive Overload Reminder</div>
          <div style={{ fontSize: 12, color: "#A16207", lineHeight: 1.5 }}>Increase weight 2–5% every 1–2 weeks. Schoenfeld et al. (2017) meta-analysis: progressive overload is the #1 driver of muscle hypertrophy. Track your weights!</div>
        </div>
      </div>
    </div>
  );
}
